import { ECountryIso2Code, ECountryIso3Code } from "~application/constants/country.constant.ts";
import { ELocale } from "~application/constants/locale.constant.ts";
import { ERouteName } from "~application/constants/routes.constant.ts";
import { DomainErrorCode } from "~domain/global/errors/domain-error.ts";
import { ESitePhotoType } from "~domain/sites/models/site-photo-model.ts";
import { SubscriberMyVoltalisAccess } from "~domain/subscribers/models/subscriber-model.ts";

const myvoltalis: Record<SubscriberMyVoltalisAccess, string> = {
  [SubscriberMyVoltalisAccess.MY_VOLTALIS_2016]: "MyVoltalis 2016",
  [SubscriberMyVoltalisAccess.MY_VOLTALIS_2022]: "MyVoltalis 2022",
};

const breadcrumb: Record<ERouteName, string> = {
  [ERouteName.ROOT]: "Voltexploit",
  [ERouteName.AUTHENTICATION]: "Authentification",
  [ERouteName.AUTHENTICATION__LOGIN]: "Connexion",
  [ERouteName.AUTHENTICATION__LOGOUT]: "Déconnexion",
  [ERouteName.SEARCH_SITE]: "Recherche de site",
  [ERouteName.DEVICES_MODULATOR_DETAILS]: "Modulateur",
  [ERouteName.DEVICES_PILOTS_DETAILS]: "Pilote",
  [ERouteName.DEVICES_THIRD_PARTY_SENSORS_DETAILS]: "Capteurs tiers",
  [ERouteName.SITES_ROOT]: "Site {siteId}",
  [ERouteName.SITES_OVERVIEW]: "Vue d'ensemble",
  [ERouteName.SITES_QUALITY_CONTROL]: "Contrôle qualité",
  [ERouteName.SITES_DEVICES]: "Appareils",
  [ERouteName.SITES_GALLERY]: "Galerie",
  [ERouteName.SITES_WORKING_CONDITION]: "État de fonctionnement",
  [ERouteName.SITES_WORKING_CONDITION__ACQUISITION]: "Acquisition",
  [ERouteName.SITES_WORKING_CONDITION__MODULATION]: "Modulation",
};

const errors: Record<DomainErrorCode, string> = {
  [DomainErrorCode.BAD_REQUEST_ERROR]: "Bad Request",
  [DomainErrorCode.UNEXPECTED_ERROR]: "Erreur inattendue",
  [DomainErrorCode.FORBIDDEN_ERROR]: "Pas accès",
  [DomainErrorCode.VALIDATION_FAILED_ERROR]: "Erreur de validation",
  [DomainErrorCode.FIELD_MISSING_ERROR]: "Champ manquant",
  [DomainErrorCode.NOT_FOUND_ERROR]: "Non trouvé",
  [DomainErrorCode.FILE_UPLOAD_ERROR]: "Erreur lors de l'envoi",
  [DomainErrorCode.TRANSPORT_ERROR]: "Erreur API",
  [DomainErrorCode.INVALID_ARGUMENT_ERROR]: "Erreur de validation",
  [DomainErrorCode.DEVICE_NOT_FOUND_ERROR]: "Appareil non trouvé",
  [DomainErrorCode.DEVICE_ALREADY_ON_SITE_ERROR]: "Appareil déjà sur site",
  [DomainErrorCode.DEVICE_NOT_LINKED_TO_SITE_ERROR]: "Appareil non lié au site",
  [DomainErrorCode.DEVICE_HAS_WRONG_TYPE_ERROR]: "Mauvais type d'appareil",
  [DomainErrorCode.DEVICE_HAS_NO_HW_LINK_ERROR]: "Appareil sans HW Link",
  [DomainErrorCode.VX_UNKNOWN_DEVICE_ERROR]: "Appareil inconnu",
  [DomainErrorCode.GATEWAY_NOT_LINKED_TO_SITE_ERROR]: "Pilote non lié au site",
  [DomainErrorCode.GATEWAY_UNAVAILABLE_ERROR]: "Pilote indisponible",
  [DomainErrorCode.DEVICE_HAS_WRONG_MODE_ERROR]: "Mauvais mode d'appareil",
  [DomainErrorCode.COMMUNICATION_STATE_NOT_FOUND_ERROR]: "Erreur de communication",
  [DomainErrorCode.GATEWAY_UPDATE_FAILED_ERROR]: "Erreur lors de la mise à jour de pilote",
  [DomainErrorCode.MISSING_OPERATOR_COMPANY_ERROR]: "Opérateur sans entreprise",
  [DomainErrorCode.OPERATION_NOT_FOUND_ERROR]: "Opération manquante",
  [DomainErrorCode.SITE_OPERATION_LINK_NOT_FOUND_ERROR]: "Site sans opération",
  [DomainErrorCode.SITE_HAS_MULTIPLE_MISSIONS_ERROR]: "Le site possède plusieurs missions",
  [DomainErrorCode.SITE_IS_ATTACHED_TO_MULTIPLE_OPERATIONS_ERROR]: "Le site est attaché à plusieurs opérations",
  [DomainErrorCode.OPERATION_MISSION_NOT_FOUND_ERROR]: "Mission de l'opération non trouvée",
};

const photoType: Record<ESitePhotoType, string> = {
  [ESitePhotoType.DELIVERY_POINT]: "Point de livraison",
  [ESitePhotoType.CONTRACT]: "Contrat",
  [ESitePhotoType.BEFORE_INSTALL_CLOSED]: "Avant installation - Fermé",
  [ESitePhotoType.BEFORE_INSTALL_OPENED]: "Avant installation - Ouvert",
  [ESitePhotoType.AFTER_INSTALL_CLOSED]: "Après installation - Fermé",
  [ESitePhotoType.AFTER_INSTALL_OPENED]: "Après installation - Ouvert",
  [ESitePhotoType.OTHER]: "Autre",
  [ESitePhotoType.RADIATORS]: "Radiateurs",
  [ESitePhotoType.INSTALLATION_SHEET_VOLTA_PAP]: "Fiche d'installation VOLTAPAP",
  [ESitePhotoType.ELECTRICAL_BOARD]: "Tableau électrique",
  [ESitePhotoType.INSTALLATION_SHEET_VOLT_INSTALL]: "Fiche d'installation VOLTINSTALL",
  [ESitePhotoType.CASKET]: "Caisse",
  [ESitePhotoType.INSTALL_BEFORE_INSTALL]: "Installation - Avant installation",
  [ESitePhotoType.INSTALL_AFTER_WIRING]: "Installation - Après câblage",
  [ESitePhotoType.INSTALL_AFTER_REASSEMBLY]: "Installation - Après réassemblage",
  [ESitePhotoType.UNINSTALL_BEFORE_UNINSTALL]: "Désinstallation - Avant désinstallation",
  [ESitePhotoType.UNINSTALL_AFTER_REASSEMBLY]: "Désinstallation - Après réassemblage",
  [ESitePhotoType.BEFORE_WIRING]: "Avant câblage",
  [ESitePhotoType.INITIAL_INVENTORY]: "Inventaire initial",
  [ESitePhotoType.APPOINTMENT_COMMITMENT_FORM]: "Formulaire d'engagement de rendez-vous",
  [ESitePhotoType.ELECTRICITY_BILL]: "Facture d'électricité",
  [ESitePhotoType.CEE_QUOTE]: "Devis CEE",
  [ESitePhotoType.NEW_IMPORT_INSTALLATION_SHEET]: "Fiche d'installation",
  [ESitePhotoType.PROGRAMMING_MODE_STICKER]: "Sticker mode de programmation",
  [ESitePhotoType.WIRE_MODE]: "Mode fil pilote",
  [ESitePhotoType.NAME_PLATE]: "Plaque signalétique",
  [ESitePhotoType.ELECTRICAL_BOARD_AND_HOUSING]: "Tableau & Logement",
  [ESitePhotoType.RADIATORS_AND_TOWEL_DRYERS]: "Radiateurs & Sèches Serviettes",
  [ESitePhotoType.HOT_WATER_TANK]: "Ballon d'eau chaude",
  [ESitePhotoType.UNKNOWN]: "Inconnu",
};

export const COMMON_FRENCH_LOCALE = {
  myvoltalis: myvoltalis,
  breadcrumb: breadcrumb,
  errors: errors,
  languages: {
    [ELocale.FRENCH]: "Français",
    [ELocale.ENGLISH]: "English",
  },
  countries: {
    iso2: {
      [ECountryIso2Code.FR]: "France",
      [ECountryIso2Code.GB]: "Grande-Bretagne",
      [ECountryIso2Code.SE]: "Suède",
      [ECountryIso2Code.FI]: "Finlande",
      [ECountryIso2Code.ES]: "Espagne",
      [ECountryIso2Code.PR]: "Portugal",
      [ECountryIso2Code.SA]: "Arabie saoudite",
      [ECountryIso2Code.AE]: "Émirats arabes unis",
    },
    iso3: {
      [ECountryIso3Code.FRA]: "France",
      [ECountryIso3Code.GBR]: "Grande-Bretagne",
      [ECountryIso3Code.SWE]: "Suède",
      [ECountryIso3Code.FIN]: "Finlande",
      [ECountryIso3Code.ESP]: "Espagne",
      [ECountryIso3Code.PRT]: "Portugal",
      [ECountryIso3Code.SAU]: "Arabie saoudite",
      [ECountryIso3Code.ARE]: "Émirats arabes unis",
    },
  },
  photoType: photoType,
  misc: {
    seeMore: "Voir plus",
    search: "Rechercher...",
    searchBy: "Rechercher par {field}...",
    emptyResults: "Aucun résultat trouvé...",
    modulator: "Modulateur",
    pilot: "Pilote",
    size: {
      small: "Petit",
      medium: "Moyen",
      large: "Grand",
    },
    sortBy: "Trier par",
    asc: "Ascendant",
    desc: "Descendant",
    yes: "Oui",
    no: "Non",
  },
};
